import { Controller } from "@hotwired/stimulus"
import { fetchStream } from "../lib/stream_fetch"

export default class extends Controller {
  static targets = ["grid", "toolbar", "count", "all"]
  static values = { deleteUrl: String, moveUrl: String, copyUrl: String, restoreUrl: String, destroyUrl: String, addTagsUrl: String, removeTagsUrl: String }

  connect() {
    this.selectedIds = new Set()
    this.interceptClick = this.interceptClick.bind(this)
    this.gridTarget.addEventListener("click", this.interceptClick, true)
    this.handleReset = () => this.exitBulkMode()
    window.addEventListener("bulk-select:reset", this.handleReset)
    this.handleKeydown = this.handleKeydown.bind(this)
    window.addEventListener("keydown", this.handleKeydown)
    // Turbo snapshots the live DOM when the next visit starts: a captured
    // selection would restore with the toolbar and pressed cards out of
    // step with this Set, which reconnects empty and so clears none of them
    this.beforeCache = () => this.clearSelections()
    document.addEventListener("turbo:before-cache", this.beforeCache)
    // Infinite scroll appends rows unselected, which turns "all" into "some".
    // Grid children only: syncUI writes the count text, and observing that
    // node too would re-fire the observer forever.
    this.observer = new MutationObserver(() => this.syncUI())
    this.observer.observe(this.gridTarget, { childList: true })
  }

  disconnect() {
    this.observer.disconnect()
    this.gridTarget.removeEventListener("click", this.interceptClick, true)
    window.removeEventListener("bulk-select:reset", this.handleReset)
    window.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("turbo:before-cache", this.beforeCache)
  }

  // Esc has a layered meaning: the browser already closes any open <dialog>
  // (modals/drawers) natively. Only when nothing else is on top do we want
  // Esc to clear the bulk selection (linkbucket#2433).
  handleKeydown(event) {
    if (event.key !== "Escape") return
    if (!this.isActive) return
    if (document.querySelector("dialog[open]")) return
    this.clearSelections()
  }

  interceptClick(event) {
    if (!this.isActive) return
    const card = event.target.closest("[data-bulk-select-id]")
    if (!card) return
    if (event.target.closest("[data-action*='bulk-select#toggle']")) return

    const id = this.extractId(card)
    if (!id) return

    event.preventDefault()
    event.stopPropagation()
    this.toggleId(id)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.toggleId(String(event.params.id))
  }

  // The header checkbox: selects every loaded row, or clears when all are.
  // Rows are marked in one pass and the UI synced once, so selecting n rows
  // is n updates rather than n grid scans.
  toggleAll() {
    const ids = this.loadedIds
    if (ids.every(id => this.selectedIds.has(id))) {
      this.clearSelections()
    } else {
      ids.forEach(id => this.selectId(id))
      this.syncUI()
    }
  }

  toggleId(id) {
    const cardEl = this.findCard(id)
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id)
      cardEl?.classList.remove("card--selected")
    } else {
      this.selectedIds.add(id)
      cardEl?.classList.add("card--selected")
    }
    this.syncSelectState(cardEl)
    this.syncUI()
  }

  selectId(id) {
    if (this.selectedIds.has(id)) return
    const cardEl = this.findCard(id)
    this.selectedIds.add(id)
    cardEl?.classList.add("card--selected")
    this.syncSelectState(cardEl)
  }

  // card--selected is the visual state; aria-pressed is the same state for
  // assistive tech, which can't see the class. Both change in exactly two
  // places, so they're kept in step here rather than at each call site.
  syncSelectState(cardEl) {
    const button = cardEl?.querySelector(".card__select")
    button?.setAttribute("aria-pressed", cardEl.classList.contains("card--selected"))
  }

  cancel() {
    this.clearSelections()
  }

  exitBulkMode() {
    this.clearSelections()
  }

  clearSelections() {
    this.selectedIds.forEach(id => {
      const cardEl = this.findCard(id)
      cardEl?.classList.remove("card--selected")
      this.syncSelectState(cardEl)
    })
    this.selectedIds.clear()
    this.syncUI()
  }

  syncUI() {
    const active = this.isActive
    this.element.classList.toggle("bulk-select--active", active)
    this.toolbarTarget.hidden = !active
    this.countTarget.textContent = this.selectedIds.size
    if (this.hasAllTarget) this.allTarget.setAttribute("aria-pressed", this.allState)
  }

  get isActive() {
    return this.selectedIds.size > 0
  }

  get loadedIds() {
    return Array.from(this.gridTarget.querySelectorAll("[data-bulk-select-id]"), el => el.dataset.bulkSelectId)
  }

  // aria-pressed on the header checkbox: "mixed" when only some loaded rows are selected.
  get allState() {
    const ids = this.loadedIds
    const selected = ids.filter(id => this.selectedIds.has(id)).length
    if (selected === 0) return "false"
    return selected === ids.length ? "true" : "mixed"
  }

  extractId(card) {
    return card.dataset.bulkSelectId
  }

  findCard(id) {
    return this.gridTarget.querySelector(`[data-bulk-select-id="${id}"]`)
  }

  buildFormData() {
    const body = new FormData()
    const byType = {}
    let hasTypedSelections = false

    this.selectedIds.forEach(id => {
      const type = this.findCard(id)?.dataset.bulkSelectType
      if (type) {
        hasTypedSelections = true
        byType[type] = byType[type] || []
        byType[type].push(id)
      }
    })

    if (hasTypedSelections) {
      Object.entries(byType).forEach(([type, ids]) => {
        ids.forEach(id => body.append(`${type}_ids[]`, id))
      })
    } else {
      this.selectedIds.forEach(id => body.append("ids[]", id))
    }

    return body
  }

  async bulkRestore() {
    await this.submitAction(this.restoreUrlValue, "PATCH")
  }

  async bulkDestroy() {
    await this.openDialog(this.destroyUrlValue)
  }

  async bulkDelete() {
    await this.openDialog(this.deleteUrlValue)
  }

  async bulkMove() {
    await this.openDialog(this.moveUrlValue)
  }

  async bulkCopy() {
    await this.openDialog(this.copyUrlValue)
  }

  async bulkAddTags() {
    await this.openDialog(this.addTagsUrlValue)
  }

  async bulkRemoveTags() {
    await this.openDialog(this.removeTagsUrlValue)
  }

  async submitAction(url, method) {
    const response = await fetchStream(url, { method, body: this.buildFormData() })
    if (!response) return

    if (response.ok) {
      const html = await response.text()
      Turbo.renderStreamMessage(html)
    } else {
      // A silent non-2xx is a dead click (#2583): the dialog just never
      // opens, or the action appears to do nothing.
      this.showErrorToast()
    }
  }

  showErrorToast() {
    const container = document.getElementById("toast-container")
    if (!container) return

    const toast = document.createElement("div")
    toast.className = "toast"
    toast.dataset.controller = "toast"
    toast.setAttribute("data-turbo-temporary", "")
    toast.setAttribute("role", "status")
    toast.setAttribute("aria-live", "polite")
    toast.textContent = "That didn't work. Please try again."
    container.appendChild(toast)
  }

  async openDialog(url) {
    await this.submitAction(url, "POST")
  }
}
